export const NUR_AI_LICENSE_TEXT = `
AI-Generated Content License Agreement

  1. Definitions
  1.1 “AI-Generated Content / AI Image” means any visual content generated using IndiaPicture’s AI tools, including content created through third-party AI engines integrated into the platform.
  1.2 “Unreviewed AI Image” means an AI image downloaded immediately after generation, without undergoing IndiaPicture’s compliance review. These images are permitted only for internal evaluation and are not cleared for external or commercial use.
  1.3 “Reviewed / Approved AI Image” means an AI image that has passed IndiaPicture’s compliance review, is approved for commercial use, and is issued with a Commercial AI License Certificate.
  1.4 “Synthetic Human” means an AI-generated person created by the system who may unintentionally resemble a real individual.
  1.5 “User Input” means all prompts, text, uploads, references, and any material submitted by the user to generate AI outputs.
  1.6 “Commercial Use” means any public-facing, monetized, promotional, published, or client-facing use, including ads, marketing, print, digital, packaging, editorial, social media, apps, websites, and broadcast.

  2. License Framework for AI-Generated Content

  2.1 Unreviewed AI Images — Internal Use Only
  Unreviewed AI images may be used strictly for:
    • Internal drafts, exploration, and creative development
    • Internal team review
    • Mood boards or concept ideation
    • Non-public, non-client, non-distributed internal use

  Unreviewed images may NOT be used for:
    • Advertising, marketing, branding, packaging
    • Social media, websites, apps, or digital publishing
    • Editorial, print, outdoor, or broadcast
    • Client presentations, pitches, proposals, activation decks
    • Sharing with clients, agencies, freelancers, vendors, or partners
    • WhatsApp, email, cloud sharing, or any public posting

  Unreviewed images include:
    • No commercial rights
    • No legal clearance or warranties
    • No indemnification

  2.2 Reviewed & Approved AI Images — Commercial License
  AI images approved through IndiaPicture’s compliance review:
    • Are cleared for commercial usage
    • May be used across digital, print, broadcast, OOH, packaging, websites, social media, apps, and promotional materials
    • Are accompanied by a Commercial AI License Certificate
    • May be eligible for indemnification only as provided under the integrated provider’s standard API licensing for licensed generated assets
    • Are provided in upscaled resolution (when available)

  Rejected images remain restricted to internal use only.

  3. Non-Exclusivity & Non-Uniqueness
    • AI images are non-exclusive and may resemble outputs generated by other users.
    • IndiaPicture does not guarantee originality or uniqueness of any AI output.
    • IndiaPicture is not responsible for conceptual similarity across users due to shared AI models.

  4. Prohibited AI Uses
  Users may NOT generate, attempt to generate, or use AI images involving:

  4.1 Identity & Personal Rights
    • Public figures, celebrities, ministers, influencers, or recognizable individuals
    • Deepfake-style outputs
    • Synthetic humans resembling real persons
    • Attempts to recreate real people from descriptions or reference images

  4.2 Copyright & Trademarks
    • Logos, brand names, trademarks, copyrighted characters
    • Known artistic styles that are copyrighted or trademarked
    • Uploading copyrighted material without permission
    • Attempts to replicate branded products

  4.3 Sensitive Content
    • Adult, explicit, or sexual content
    • Violence, hate, discrimination, or harassment
    • Political campaigns or ideologically sensitive material
    • Religious disrespect, extremist content, or harmful propaganda
    • Minors in sensitive or dangerous contexts
    • Medical, legal, financial, or regulated-industry claims

  IndiaPicture may block or penalize any attempt to generate restricted content.

  5. Modification Restrictions
    • Editing or modifying an AI image does not expand or change the licensed rights.
    • Any risky or misleading modification voids indemnification (if any is available under provider terms).
    • Significant edits may require resubmission for compliance review.

  6. Sharing & Distribution Restrictions
  Users may NOT share Unreviewed AI Images:
    • With clients or external stakeholders
    • In presentations, proposals, or published materials
    • On social media, websites, or public platforms
    • With freelancers, vendors, third-party agencies
    • Via WhatsApp, email, or cloud links

  Sharing unreviewed images externally is treated as public use and is strictly prohibited.

  7. AI Training Restrictions
  Users may NOT:
    • Use IndiaPicture AI outputs to train or improve AI or machine-learning systems
    • Build datasets using AI-generated outputs
    • Sell or redistribute IndiaPicture AI outputs for training purposes

  This restriction is absolute and non-negotiable.

  8. Compliance Review Process

  8.1 Submission
  User submits selected images for review.

  8.2 Evaluation
  IndiaPicture reviews for:
    • Public-figure or real-person resemblance
    • Copyright or trademark conflicts
    • Risky content or sensitive categories
    • AI distortions, biased representations, or unsafe elements
    • General compliance and legal safety

  8.3 Possible Outcomes
    • Approved → Commercial AI License Certificate issued
    • Rejected → Internal-use only
    • Returned for edits → Changes required before resubmission

  8.4 Disclaimer
  Approval signifies a reasonable compliance check, not a guarantee of zero legal risk.

  9. User Warranties
  Users confirm and warrant that:
    • All prompts and uploads are legally owned or appropriately licensed
    • No copyrighted or protected materials are uploaded
    • They comply with all applicable laws
    • They will not engage in prohibited uses
    • They indemnify IndiaPicture against losses arising from user violations

  10. Accuracy, Bias & Representation Disclaimer
  AI outputs may:
    • Contain errors, distortions, or unrealistic visual details
    • Reflect model biases or inaccuracies
    • Misrepresent human features, ethnicity, or attributes
    • Generate unpredictable artistic interpretations

  User is responsible for verifying suitability before use.

  11. Indemnification

  11.1 No Indemnification for Unreviewed / Preview Outputs
  Unreviewed AI Images (including preview outputs) are provided for internal evaluation only and include no indemnification.

  11.2 Licensed Generated Assets (If Applicable)
  Where an AI-generated image is a licensed generated asset obtained through IndiaPicture’s integrated provider workflow, any indemnification available is provided only under the provider’s standard API licensing terms applicable to that licensed asset.

  11.3 No IndiaPicture Indemnity Amount / Cap
  IndiaPicture does not provide a separate indemnification amount, cap, or guarantee. Any indemnification rights, conditions, limitations, and exclusions are solely as defined by the applicable provider license for the specific licensed generated asset.

  11.4 Indemnification Exclusions
  Indemnification (if applicable under provider terms) will not apply to:
    (a) Unreviewed/preview outputs;
    (b) prohibited uses or prohibited prompts;
    (c) infringement arising from User Inputs (including uploaded references) that the User does not own or have rights to use;
    (d) use suggesting endorsement, defamation, or misleading claims;
    (e) material modification of the licensed asset beyond what is permitted under the provider terms;
    (f) use in violation of applicable law or this EULA.

  12. Limitations of Liability
  IndiaPicture is not liable for:
    • Misuse of AI outputs by the user
    • Any resemblance between AI outputs and real individuals
    • Consequences arising from user prompts or uploads
    • Damages, losses, or claims resulting from improper use
    • Technical issues, generation errors, or platform downtime
    • Violations of third-party rights caused by user input

  13. Termination of License
  IndiaPicture may revoke licensing and restrict user access if:
    • The user attempts to generate prohibited content
    • The user misuses unreviewed images
    • The user uploads copyrighted or illegal materials
    • The user attempts to bypass compliance review
    • The user trains external AI systems with IndiaPicture outputs
    • Repeated violations of this EULA occur

  Termination does not require prior notice in high-risk cases.
`;
